import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getBousalaReport, generateBousalaReport } from "@/lib/bousala/report.functions";
import { getBousalaAttemptAdmin } from "@/lib/bousala/admin.functions";
import { BousalaReportView } from "@/components/bousala/report/report-view";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowRight, RefreshCw, ExternalLink, FileText, Users } from "lucide-react";
import { toast } from "sonner";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { asLang, type Lang } from "@/lib/bousala/i18n";

type AdminAttempt = {
  id: string;
  client_name: string | null;
  status: string;
  tier: "free" | "full" | "premium";
  started_at: string;
  completed_at: string | null;
};

export const Route = createFileRoute("/_authenticated/admin/bousala/attempts/$attemptId")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "معاينة تقرير المحاولة — إدارة" },
      { name: "robots", content: "noindex" },
    ],
  }),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: AdminAttemptPreviewPage,
});

function AdminAttemptPreviewPage() {
  const { attemptId } = Route.useParams();
  const router = useRouter();
  const fetchReport = useServerFn(getBousalaReport);
  const fetchAttempt = useServerFn(getBousalaAttemptAdmin);
  const fnGenerate = useServerFn(generateBousalaReport);

  const attempt = useQuery({
    queryKey: ["admin", "bousala", "attempt", attemptId],
    queryFn: () => fetchAttempt({ data: { attemptId } }),
  });

  const q = useQuery({
    queryKey: ["admin", "bousala", "report", attemptId],
    queryFn: () => fetchReport({ data: { attemptId } }),
  });

  const generate = useMutation({
    mutationFn: () => fnGenerate({ data: { attemptId } }),
    onSuccess: () => {
      toast.success("تم توليد التقرير");
      attempt.refetch();
      q.refetch();
      router.invalidate();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "حدث خطأ"),
  });

  const refresh = () => {
    attempt.refetch();
    q.refetch();
    router.invalidate();
  };

  const loading = attempt.isLoading || q.isLoading;
  const error = attempt.error ?? q.error;
  const attemptData = attempt.data;
  const reportData = q.data;
  const attemptLang: Lang = asLang((reportData as any)?.language ?? "ar");

  return (
    <div dir="rtl" className="space-y-6">
      <TopBar attemptId={attemptId} onRefresh={refresh} refreshing={q.isFetching || attempt.isFetching} />

      {loading ? (
        <div className="rounded-ds-lg bg-card py-24 text-center shadow-soft">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="space-y-4 py-8 text-center">
            <p className="font-display text-lg font-bold text-foreground">تعذر فتح المحاولة</p>
            <p className="text-sm text-muted-foreground">
              لم نتمكن من تحميل بيانات المحاولة الآن. لم يتم حذف أي سجل؛ جرّب تحديث الصفحة.
            </p>
            <p className="text-xs text-destructive">{(error as Error).message}</p>
            <Button type="button" variant="secondary" onClick={refresh}>تحديث المحاولة</Button>
          </CardContent>
        </Card>
      ) : !attemptData ? null : !reportData?.model ? (
        <AttemptStatusPanel
          attempt={attemptData}
          reportStatus={reportData?.status ?? attemptData.status}
          generating={generate.isPending}
          onGenerate={() => generate.mutate()}
        />
      ) : (
        <div className="space-y-4">
          <AttemptSummary attempt={attemptData} />
          <AdminReportActions
            attemptId={attemptId}
            generating={generate.isPending}
            onRegenerate={() => generate.mutate()}
          />
          <div className="py-8 px-4">
            <BousalaReportView model={reportData.model} tier="premium" attemptId={attemptId} surface="admin" stats={reportData.stats} lang={attemptLang} />
          </div>
        </div>
      )}
    </div>
  );
}

function AttemptSummary({ attempt }: { attempt: AdminAttempt }) {
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-4 py-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <p className="font-display font-bold">{attempt.client_name?.trim() || "مستخدم بدون اسم"}</p>
            <StatusPill status={attempt.status} />
            <TierPill tier={attempt.tier} />
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground" dir="ltr">{attempt.id}</p>
        </div>
        <div className="text-xs text-muted-foreground">
          بدأ: {fmtDate(attempt.started_at)} · اكتمل: {fmtDate(attempt.completed_at)}
        </div>
      </CardContent>
    </Card>
  );
}

function AttemptStatusPanel({
  attempt,
  reportStatus,
  generating,
  onGenerate,
}: {
  attempt: AdminAttempt;
  reportStatus: string;
  generating: boolean;
  onGenerate: () => void;
}) {
  const canGenerate = reportStatus === "scored" || reportStatus === "completed";
  const title =
    reportStatus === "in_progress"
      ? "المحاولة لم تكتمل بعد"
      : canGenerate
      ? "المحاولة مصحّحة وتحتاج توليد تقرير"
      : "لا يوجد تقرير محفوظ لهذه المحاولة";
  const message =
    reportStatus === "in_progress"
      ? "هذا السجل موجود، لكن المستخدم لم ينهِ الاختبار بعد. عند اكتماله سيظهر هنا خيار توليد التقرير."
      : canGenerate
      ? "يمكن توليد التقرير الآن من داخل لوحة الإدارة بدون الانتقال إلى البورتال."
      : "السجل موجود، لكن لا توجد نتيجة كافية لتوليد التقرير في هذه اللحظة.";

  return (
    <div className="space-y-4">
      <AttemptSummary attempt={attempt} />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5 text-muted-foreground" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm leading-relaxed text-muted-foreground">{message}</p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={onGenerate} disabled={!canGenerate || generating}>
              {generating ? <Loader2 className="ms-1 h-4 w-4 animate-spin" /> : <RefreshCw className="ms-1 h-4 w-4" />}
              توليد التقرير
            </Button>
            <Button asChild variant="secondary">
              <Link to="/admin/bousala/attempts" search={{ status: "all", tier: "all", language: "all", q: "", hideTest: false, page: 1 }}>العودة للمحاولات</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AdminReportActions({
  attemptId,
  generating,
  onRegenerate,
}: {
  attemptId: string;
  generating: boolean;
  onRegenerate: () => void;
}) {
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-2 py-4">
        <Button type="button" size="sm" variant="secondary" onClick={onRegenerate} disabled={generating}>
          {generating ? <Loader2 className="ms-1 h-4 w-4 animate-spin" /> : <RefreshCw className="ms-1 h-4 w-4" />}
          إعادة توليد التقرير
        </Button>
        <Button asChild size="sm" variant="ghost">
          <Link to="/portal/bousala/$attemptId" params={{ attemptId }}>
            <ExternalLink className="ms-1 h-4 w-4" />
            فتح واجهة المستخدم
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function StatusPill({ status }: { status: string }) {
  const label: Record<string, string> = {
    in_progress: "قيد الإجراء",
    completed: "مكتمل",
    scored: "مصحّح",
    report_ready: "التقرير جاهز",
  };
  return <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold text-foreground">{label[status] ?? status}</span>;
}

function TierPill({ tier }: { tier: string }) {
  const label: Record<string, string> = { free: "مجاني", full: "كامل", premium: "بريميوم" };
  return <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold text-muted-foreground">{label[tier] ?? tier}</span>;
}

function fmtDate(s: string | null) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("ar-u-nu-latn", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return s;
  }
}

function TopBar({
  attemptId,
  onRefresh,
  refreshing,
}: {
  attemptId: string;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <Link
          to="/admin/bousala/attempts"
          search={{ status: "all", tier: "all", language: "all", q: "", hideTest: false, page: 1 }}
          className="inline-flex items-center gap-1 text-xs font-bold text-muted-foreground hover:text-foreground"
        >
          <ArrowRight className="h-3.5 w-3.5" />
          العودة لقائمة المحاولات
        </Link>
        <h1 className="mt-1 font-display text-2xl font-extrabold">معاينة التقرير</h1>
        <p className="mt-0.5 text-[11px] text-muted-foreground" dir="ltr">
          {attemptId}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={onRefresh} disabled={refreshing}>
          <RefreshCw className={`ms-1 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          تحديث
        </Button>
        <Button asChild size="sm" variant="secondary">
          <Link to="/portal/bousala/$attemptId" params={{ attemptId }}>
            <ExternalLink className="ms-1 h-4 w-4" />
            فتح كما يراها المستخدم
          </Link>
        </Button>
      </div>
    </div>
  );
}